import { useCallback, useEffect, useMemo, useState } from 'react';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import { CapTableDetailsModel, WaterfallService } from 'api';
import { CapTableVersionWithCompanyId, ChildCompany } from 'common/types/company';
import { Fund } from 'common/types/fund';
import { ScalarSpreadsheetConfigDataInterface } from 'models/interfaces/spreadsheet-types/core-types';
import { ISO_DATE_FORMAT, MOMENT_DEFAULT_DATE_FORMAT, sumArrayValues } from 'utilities';
import calculateOwnersSharesValue from './calculateOwnersSharesValue';
import {
  RelatedEntitiesWaterfalls,
  WeightedShareValue,
} from '../components/FundFirmDistribution/FundFirmDistribution.props';
import { ScenarioWithCompanyId } from '../types';

const useRelatedEntitiesWaterfalls = ({
  updatedWeightedShareValues,
  childrenCapTables,
  exitDate,
  fundList,
  childrenCompanies,
  mainFundOwnershipRef,
}: {
  updatedWeightedShareValues: WeightedShareValue[] | undefined;
  childrenCapTables: CapTableVersionWithCompanyId[];
  exitDate: string;
  fundList: Fund[];
  childrenCompanies: ChildCompany[];
  mainFundOwnershipRef: React.RefObject<CapTableDetailsModel['fund_ownership'] | null>;
}) => {
  const [relatedEntitiesWaterfalls, setRelatedEntitiesWaterfalls] = useState<RelatedEntitiesWaterfalls | null>(null);
  const [childrenSheets, setChildrenSheets] = useState<Record<number, ScalarSpreadsheetConfigDataInterface>>({});

  const registerChildrenSheets = useCallback((sheets: ScalarSpreadsheetConfigDataInterface[], idKey: number) => {
    setChildrenSheets(
      prevState => ({ ...prevState, [idKey]: sheets }) as Record<number, ScalarSpreadsheetConfigDataInterface>
    );
  }, []);
  const resetChildrenSheets = useCallback(() => {
    setChildrenSheets({});
  }, []);
  const buildRelatedEntitiesWaterfallsMap = (
    results: PromiseSettledResult<ScenarioWithCompanyId>[]
  ): RelatedEntitiesWaterfalls => results.reduce((accum, res) => {
    if (res.status === 'fulfilled') {
      const { companyId, ...rest } = res.value;
      // eslint-disable-next-line no-param-reassign
      accum[companyId] = rest;
    }
    return accum;
  }, {} as RelatedEntitiesWaterfalls);

  const relatedEntitiesEquity = useMemo(() => {
    const shareValuesForRelatedEntities = updatedWeightedShareValues?.filter(ws => ws.owner === 'related_entity') ?? [];
    if (shareValuesForRelatedEntities.length === 0) {
      return null;
    }
    return shareValuesForRelatedEntities.reduce(
      (acc, curr) => {
        acc[curr.fund.id] = sumArrayValues(curr.share_values.map(sv => sv.per_share * sv.shares));
        return acc;
      },
      {} as Record<number, number>
    );
  }, [updatedWeightedShareValues]);

  useEffect(() => {
    if (relatedEntitiesEquity == null) {
      setRelatedEntitiesWaterfalls(null);
      return undefined;
    }

    let isCurrentRequest = true;
    const requestsData = Object.entries(relatedEntitiesEquity)
      .map(([relatedEntityId, equityValue]) => {
        const capTable = childrenCapTables.find(ct => ct.company_id === Number(relatedEntityId));
        if (!capTable || equityValue === 0 || capTable.is_empty) {
          return null;
        }
        return {
          capTable: Number(capTable.id),
          equityValue: Number(equityValue),
          isEmpty: capTable.is_empty,
          companyId: Number(relatedEntityId),
        };
      })
      .filter(requestData => requestData !== null);
    const parsedExitDate = moment(exitDate, [MOMENT_DEFAULT_DATE_FORMAT, ISO_DATE_FORMAT], true);
    if (!parsedExitDate.isValid() || requestsData.length === 0) {
      setRelatedEntitiesWaterfalls({});
      return undefined;
    }
    const exitDateString = parsedExitDate.format(ISO_DATE_FORMAT);

    // Hide results from the previous company/date while the new context is resolving.
    setRelatedEntitiesWaterfalls(null);

    const requestAndSetRelatedEntitiesWaterfalls = async () => {
      // `/api/waterfall` always runs liquidation waterfall math per cap table.
      const requestsToRun = requestsData.map(data =>
        WaterfallService.apiWaterfallCreate({
          captableId: data.capTable,
          equityValue: data.equityValue.toString(),
          exitDate: exitDateString,
          scenarioMethod: 0,
          scenarioType: 1,
        }).then(result => ({ ...result, companyId: data.companyId }))
      ) as Promise<ScenarioWithCompanyId>[];

      const results = await Promise.allSettled(requestsToRun);
      const relatedEntitiesWaterfallsMap = buildRelatedEntitiesWaterfallsMap(results);
      if (isCurrentRequest) {
        setRelatedEntitiesWaterfalls(relatedEntitiesWaterfallsMap);
      }
    };

    requestAndSetRelatedEntitiesWaterfalls();
    return () => {
      isCurrentRequest = false;
    };
  }, [relatedEntitiesEquity, exitDate, childrenCapTables]);

  const ownersShareValuesMap = useMemo(() => {
    if (!relatedEntitiesWaterfalls) {
      return null;
    }
    const auxMap = {} as Record<number, WeightedShareValue[]>;
    Object.entries(relatedEntitiesWaterfalls).forEach(([relatedEntityId, waterfallData]) => {
      const capTable = childrenCapTables.find(ct => ct.company_id === Number(relatedEntityId));
      if (!capTable) {
        return;
      }
      const sortedPresentValues = orderBy(waterfallData.present_values, ['security.id'], ['desc']);
      const ownerSharesValue = calculateOwnersSharesValue({
        sortedPresentValues,
        fundList,
        capTableDetail: capTable,
        childrenCapTables,
        childrenCompanies,
        mainFundOwnershipRef,
        entityReferenceId: Number(relatedEntityId),
      });
      auxMap[Number(relatedEntityId)] = ownerSharesValue;
    });
    return auxMap;
  }, [relatedEntitiesWaterfalls, fundList, childrenCapTables, childrenCompanies, mainFundOwnershipRef]);

  return {
    ownersShareValuesMap,
    relatedEntitiesWaterfalls,
    childrenSheets,
    registerChildrenSheets,
    resetChildrenSheets,
  };
};

export default useRelatedEntitiesWaterfalls;
